// src/OnboardingBanner.jsx
import { useMemo } from "react";
import { doc, setDoc } from "firebase/firestore";
import { SETUP_STEPS, FEATURE_STEPS, stepIndex } from "./onboardingConfig";

const GREEN = "#00B074";

export default function OnboardingBanner({
  phase,           // "setup" | "features" | "mfa_required" | null
  user,
  userDoc,
  db,
  navigate,
  freshEmailVerified,   // NEW
  onResendEmail,        // NEW (function)
}) {
  const setupStep = userDoc?.onboarding?.setupStep;
  const featuresTour = userDoc?.onboarding?.featuresTour;
  const featureStep = userDoc?.onboarding?.featureStep;

  const currentSetup = useMemo(() => SETUP_STEPS.find(s => s.key === setupStep), [setupStep]);
  const currentFeature = useMemo(() => FEATURE_STEPS.find(s => s.key === featureStep), [featureStep]);

  // ——— HARD GATE: Obnoxious 2FA/email banner ———
  if (phase === "mfa_required") {
    return (
      <Bar loud>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ fontWeight: 900, fontSize: 16 }}>
            You must set up <span style={{ textDecoration: "underline" }}>Two-Factor Authentication (2FA)</span> to continue.
          </div>
          <div style={{ marginTop: 6, color: "#111", fontWeight: 700 }}>
            {freshEmailVerified
              ? "After enabling 2FA, you’ll be able to navigate to other pages."
              : "First confirm your email, then enable 2FA. You’ll be able to navigate after both are complete."}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Primary onClick={() => navigate("/settings?tab=account")}>Open Settings</Primary>
          {!freshEmailVerified && (
            <Button onClick={() => onResendEmail && onResendEmail()}>
              Resend verification email
            </Button>
          )}
        </div>
      </Bar>
    );
  }

  if (phase === "setup" && currentSetup) {
    const i = stepIndex(SETUP_STEPS, setupStep);
    return (
      <Bar>
        <div>
          <strong>Complete your initial setup</strong> · Step {i + 1} of {SETUP_STEPS.length} — {currentSetup.name}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Primary onClick={() => navigate(currentSetup.route)}>Go to step</Primary>
        </div>
      </Bar>
    );
  }

  if (phase === "features" && currentFeature && (featuresTour === "active" || featuresTour === "pending")) {
    return (
      <Bar>
        <div>
          <strong>Quick tour</strong> — {currentFeature.name}
          <span style={{ color: "#475467" }}> · {currentFeature.blurb}</span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Primary onClick={() => advanceFeature()}>Next</Primary>
          <Button onClick={() => setStatus("snoozed")}>Snooze</Button>
          <Button onClick={() => setStatus("dismissed")}>Don’t show again</Button>
        </div>
      </Bar>
    );
  }

  return null;

  async function setStatus(status) {
    if (!user) return;
    const ref = doc(db, "users", user.uid);
    await setDoc(ref, { onboarding: { ...userDoc.onboarding, featuresTour: status } }, { merge: true });
  }

  async function advanceFeature() {
    if (!user) return;
    const idx = stepIndex(FEATURE_STEPS, featureStep);
    const next = FEATURE_STEPS[idx + 1];
    const ref = doc(db, "users", user.uid);
    if (next) {
      await setDoc(ref, { onboarding: { ...userDoc.onboarding, featuresTour: "active", featureStep: next.key } }, { merge: true });
      navigate(next.route);
    } else {
      await setDoc(ref, { onboarding: { ...userDoc.onboarding, featuresTour: "completed" } }, { merge: true });
    }
  }
}

function Bar({ children, loud }) {
  const base = {
    position: "sticky",
    top: 0,
    zIndex: 2000,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: "1px solid #E5E7EB",
    background: "#fff",
  };

  const loudStyle = loud
    ? {
        background: "#FFF7E6",              // warm “warning” backdrop
        borderBottom: "3px solid #F4C542",  // Trelli gold accent
        boxShadow: "0 2px 0 rgba(0,0,0,0.05)",
      }
    : {};

  return <div style={{ ...base, ...loudStyle }}>{children}</div>;
}

function Primary({ children, ...p }) {
  return (
    <button {...p} style={{ 
      height: 36, borderRadius: 10, border: "none",
      background: "#00B074", color: "#fff", fontWeight: 800, padding: "0 12px", cursor: "pointer"
    }}>
      {children}
    </button>
  );
}
function Button({ children, ...p }) {
  return (
    <button {...p} style={{
      height: 36, borderRadius: 10, border: "1px solid #E5E7EB",
      background: "#fff", color: "#111", fontWeight: 700, padding: "0 12px", cursor: "pointer"
    }}>{children}</button>
  );
}